import React, { useCallback, useEffect, useRef, useState } from 'react';

// Third party components
import {Box, Card, Stack, Container, Typography} from '@mui/material';

// Generic components
import { Button, ContainerWithCard, LineLoader, Modal, SideBar } from '@timmons-group/shared-react-components';
import ThemeMap from 'components/ThemeMap';

import StatePreference from 'components/StatePreference';

// Hooks
import { useTheme } from '@mui/material/styles';
import { useConfig, useMapConfig, useLocalStorage } from '@timmons-group/shared-react-components/hooks';

import ValuesExplorerTabPanel from 'HVRA/components/ValuesExplorerTabs';
import useCategoryDataManager from 'HVRA/hooks/useCategoryDataManager';
import useHexDataManager from 'HVRA/hooks/useHexDataManager';
import { VE_PREF_STATE_KEY, VE_FIRST_VISIT_KEY } from 'constants';


const LAYER_PREFIX = 'explorer:hvra:hexes:';
const IDENTIFY_HEADING = 'Identify';
const FEATURED_CARD_HEADING = 'Overall Values Score';
const LEVEL_5_HEXES = 'level5hexes';
const LEVEL_6_HEXES = 'level6hexes';
const LEVEL_7_HEXES = 'level7hexes';


const Explorer = () => {
  const olMap = useRef(null);
  const oThemeMap = useRef(null);

  const [mapConfig, mapConfigLoading] = useMapConfig('values_explorer');
  const [config, configLoading] = useConfig('values_explorer');
  const [aboutOpen, setAboutOpen] = useState(false);
  const [welcomeToVEOpen, setWelcomeToVEOpen] = useState(false);

  const [activeLayers, setActiveLayers] = useState([]);
  const [mapReady, setMapReady] = useState(false);
  const [selectedTab, setSelectedTab] = useState(1);
  const [forceUpdate, setForceUpdate] = useState(0);
  const theme = useTheme();
  const sideBarRef = useRef(null);
  const mapLegend = useRef([]);
  const[featData, setFeatData] = useState({
    theme: {},
    cardData:[],
    heading: {},
    featuredCard: {}
  });
  const [statePref, setStatePref] = useState({});
  const statePrefSlug = useRef('');
  const isMapViewDirty = useRef(false);
  const [shouldShowAnalyzeBtn, setShouldShowAnalyzeBtn] = useState(false);

  const themeMapInstance = useRef(null);

  const {
    categories,
    slugs,
    saveSlugs,
    categoriesInitialized,
    getFilters,
    toggleIncludeCategory,
    buildUpdatedInspectorData,
  } = useCategoryDataManager();


  const {
    hexBins,
    computeNewThreatScore,
    determineNewBin,
    computeNewHexBins
  } = useHexDataManager();

  const [firstVisit, setFirstState] = useLocalStorage(VE_FIRST_VISIT_KEY, null);

  useEffect(() => {
    if (!firstVisit) {
      setWelcomeToVEOpen(true);
    }
  }, [firstVisit]);

  useEffect(() => {
    if (!mapConfigLoading && mapConfig) {
      const layerToFetch = LAYER_PREFIX + statePrefSlug.current;
      mapLegend.current.length = 0;
      const layer = mapConfig?.layers?.find((item) => item.key === layerToFetch);
      const styleData = layer?.mvt?.endpoints[0]?.style;
      if (styleData?.dynamic?.map) {
        for (let [key, value] of Object.entries(styleData.dynamic.map)) {
          mapLegend.current.push({ score: key, legendLabel: value.label, legendColor: value.fillColor });
        }
      }
    }
  }, [mapConfigLoading, mapConfig, statePrefSlug.current]);

  const handleDrawerTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const processFeatures = (feats) => {
    // since it's single select clear any prior selections from the map
    unhightlightAllFeatures();

    if (!feats || feats.length === 0) {
      return;
    }
    // We do this because the click handler will in some cases be given a RenderFeature for a hex layer that is not currently visible
    // e.g., you're zoomed into level 7 hexes and when you click on a single hex, the click handler is passed a single hex for level 7
    // but also a hex at that same location for level 6 and level 5.  We only want to select and highlight the hex for the current
    // visible level.
    const renderedLevel = deriveRenderedHexLevel();

    // fetch the newly selected feature and highlight it on the map
    const theFeat = feats.find((feat) => feat.properties.layer === renderedLevel);
    if (theFeat) {
      highlightFeature(theFeat, true);
      // update the raw data for the feature to display in the sidebar
      const updatedFeature = buildUpdatedInspectorData(theFeat, mapLegend, theme, IDENTIFY_HEADING, FEATURED_CARD_HEADING);

      setFeatData(feat => ({...feat, ...updatedFeature}));
      setForceUpdate(forceUpdate + 1);

      // switch to the inspector tab
      setSelectedTab(0);
    }

    // open the sidebar if it's not already open
    if (sideBarRef.current) {
      sideBarRef.current.forceOpen();
    }
  };

  const handleClearButtonClick = () => {
    unhightlightAllFeatures();

    const clearFeat = {
      theme,
      cardData:[],
      heading: {},
      featuredCard: {}
    };

    setFeatData(feat => ({...feat, ...clearFeat}));
    setForceUpdate(forceUpdate + 1);
    setSelectedTab(1);
  }

  const unhightlightAllFeatures = () => {
    if (themeMapInstance.current) {
      const theLayer = LAYER_PREFIX + statePrefSlug.current;
      themeMapInstance.current.unhighlightAllFeatures(theLayer);
    }
  };

  const highlightFeature = (feature) => {
    if (themeMapInstance.current) {
      const theLayer = LAYER_PREFIX + statePrefSlug.current;
      themeMapInstance.current.highlightFeature(theLayer, feature);
    }
  };

  const deriveRightJenksColumn = () => {
    let colName = 'jenks_';
    let fieldMask = ['1','1','1','1','1'];
    if (!categories || categories.length === 0) {
      return colName + '11111';
    }
    categories.current.forEach((cat) => {
      switch (cat.slug) {
        case 'Pp':
          fieldMask[0] = cat.exclude ? '0' : '1';
          break;
        case 'Infra':
          fieldMask[1] = cat.exclude ? '0' : '1';
          break;
        case 'Dw':
          fieldMask[2] = cat.exclude ? '0' : '1';
          break;
        case 'Wl':
          fieldMask[3] = cat.exclude ? '0' : '1';
          break;
        case 'Cult':
          fieldMask[4] = cat.exclude ? '0' : '1';
          break;
      }
    });
    return colName + fieldMask.join('');
  }


  const mapTileLoaded = React.useCallback((loaderInfo) => {
    const hexes = hexBins.current;
    loaderInfo.tile.features_.forEach((f) => {
      if (isMapViewDirty.current) {
        if(hexes.length > 0) {
          computeNewThreatScore(slugs.current, f.properties_);
          f.properties_.jenksThreat = determineNewBin(f.properties_.threat, hexes);
        }
      } else {
        const jnks = deriveRightJenksColumn();
        f.properties_.jenksThreat = f.properties_[jnks];
      }
    });
  },[slugs]);

  const mapTilesMoved = () => {
    const prevDirty = isMapViewDirty.current;
    isMapViewDirty.current = false;
    setShouldShowAnalyzeBtn(true);
    if (prevDirty) {
      refreshMap();
    }
  }

  const setMap = useCallback((olm) => {
    oThemeMap.current = olm;
    // olMap is a ref to the map. So we need to get the .current property
    olMap.current = olm.olMap.current;
    setMapReady(true);

    olMap.current.on('moveend',mapTilesMoved);

    const layersToListenFor = olMap.current.themes.getCategoryByKey('explorer:hvra:cat_hex_layers').getLayersArray();
    if (layersToListenFor && layersToListenFor.length > 0) {
      layersToListenFor.forEach((layer) => {
        layer.getSource().on('tileloadend', mapTileLoaded);
      });
    }

    window.ol_map = olMap;
  });

  const handleAboutThisToolClick = useCallback(() => {
    setAboutOpen(true);
  },[]);

  const handleAboutThisToolClose = useCallback((event, reason) => {
    if (reason == 'backdropClick')
      return;
    setAboutOpen(false);
  },[]);

  const handleWelcomeToVEClose = useCallback((event, reason) => {
    if (reason == 'backdropClick')
      return;

    setFirstState(true);
    setWelcomeToVEOpen(false);
  },[]);

  const handleSideBarToggled = useCallback(() => {
    setTimeout(() => {
      olMap.current.updateSize();
    }, 100);
  },[olMap]);

  const deriveRenderedHexLevel = useCallback(() => {
    let layerKey = LEVEL_5_HEXES;
    olMap.current.themes
      .getCategoryByKey('explorer:hvra:cat_hex_layers')
      .getLayersArray().forEach((x)=>{
        if (x.rendered === true) {
          if(x?.values_?.source.key_.includes(LEVEL_6_HEXES)) {
            layerKey =  LEVEL_6_HEXES;
          } else {
            if(x?.values_?.source.key_.includes(LEVEL_7_HEXES)) {
              layerKey =  LEVEL_7_HEXES;
            }
          }
        }
      });

    return layerKey;
  },[olMap]);


  // The part of this function which determines which hexes to use for the intervals (which layer specifically)
  // has been challenging to get right.  What I've found is you cannot predict which layer offset in the array is the one that is rendered.
  // The current solution is to loop through the layers that come back and find the one that has elements in the array since in my testing
  // the other layers in this array are empty.
  const reCalcHexBins = () => {
    const threatScoresToBin = [];
    let intervals = [];
    olMap.current.themes
      .getCategoryByKey('explorer:hvra:cat_hex_layers')
      .getFeaturesInView().then((x) => {
        x.forEach((f) => {
          if (f && f.features && f.features.length > 0) {
            f.features.forEach((f) => {
              // compute new threat score for each hex
              threatScoresToBin.push(computeNewThreatScore(slugs.current, f.properties_));
            });
            intervals = computeNewHexBins(threatScoresToBin);
          }
        });

        if (intervals.length > 0) {
          hexBins.current = intervals;
          refreshMap();
        }
      });
  }

  const refreshMap = () => {
    olMap.current.themes
      .getCategoryByKey('explorer:hvra:cat_hex_layers')
      .getLayersArray().forEach((x)=>{
        if (x.rendered === true) {
          x.getSource().refresh();
        }
      });
  }


  const handleExcludeSwitchChange = (category) => {
    toggleIncludeCategory(category);
    saveSlugs();

    queryMapFilterEngine();
    //refresh the map here!!!
    refreshMap();

    // We were having a problem where the exclude switch update to the categories array was
    // not telling the child component to render right away.  As a result when you exluded a category
    // the map would update right away but the slider would not disable for like 60 seconds.
    // This is a hack to force the child component to re-render.
    setForceUpdate(forceUpdate + 1);
  }

  const handleAnalyzeThisArea = () => {
    isMapViewDirty.current = true;
    setShouldShowAnalyzeBtn(false);
    reCalcHexBins();
    queryMapFilterEngine();
  }

  const handleFilterChange = () => {
    // Not sure why but the categories array is already updated by the time this function is called.
    // so we don't need to update it here.  We just call the queryMapFilterEngine function to refilter the map.
    queryMapFilterEngine();
  }

  const handleStateDDChange = (event) => {
    let theMapCat = olMap?.current?.themes.getCategoryByKey('explorer:hvra:cat_hex_layers');
    if (theMapCat && statePref?.label) {
      // first, clear any previously selected hex since it would be from a different state
      handleClearButtonClick();

      const oldPrefSlug = statePref.label.replaceAll(' ','_').toLowerCase();
      const theOldLayer = theMapCat.getLayerByKey(`explorer:hvra:hexes:${oldPrefSlug}`);
      // some states won't have layers published yet.  So we need to check for that.
      if (theOldLayer) {
        theOldLayer.setVisible(false);
      }
    }
    if (theMapCat && event?.label) {
      const newPrefSlug = event.label.replaceAll(' ','_').toLowerCase();
      statePrefSlug.current = newPrefSlug;
      const theNewLayer = theMapCat.getLayerByKey(`explorer:hvra:hexes:${newPrefSlug}`);

      // some states won't have layers published yet.  So we need to check for that.
      if(theNewLayer) {
        theNewLayer.setVisible(true);
        if (!configLoading && config) {
          const newCat = config?.filter((item)=>{return item.client === newPrefSlug});
          if (newCat) {
            categories.current = categoriesInitialized(newCat[0].valuesExplorerCats);
            const al = `explorer:hvra:hexes:${newPrefSlug}`;
            setActiveLayers([al]);
          }
        }
      } else {
        categories.current = categoriesInitialized([]);
      }
    }

    setStatePref(event);
  }

  const queryMapFilterEngine = () => {
    const { ppRange, infraRange, dwRange, wlRange, cultRange, whpRange } = getFilters();

    olMap?.current?.themes
      .getCategoryByKey('explorer:hvra:cat_hex_layers')
      .getLayerByKey(`explorer:hvra:hexes:${statePrefSlug.current}`)
      .filter
      .when('jenks_pp').inRangeInclusive(...ppRange)
      .andJoin()
      .when('jenks_infra').inRangeInclusive(...infraRange)
      .andJoin()
      .when('jenks_dw').inRangeInclusive(...dwRange)
      .andJoin()
      .when('jenks_wildlife').inRangeInclusive(...wlRange)
      .andJoin()
      .when('jenks_cult').inRangeInclusive(...cultRange)
      .andJoin()
      .when('jenks_whp').inRangeInclusive(...whpRange);
  }

  const renderAnalyzeThisAreaButton = () => {
    if (shouldShowAnalyzeBtn) {
      return (
        <Button
          label="Analyze this Area"
          color="secondary"
          size="small"
          sx={{ ml: '10px' }}
          onClick={handleAnalyzeThisArea}
        />
      );
    }
    return null;
  }

  const renderMapActions = () => {
    return (
      <>
        <Stack
          spacing={2}
          direction="row"
          sx={{ minWidth: '100%', alignItems: 'center', minHeight: '55px' }}
        >
          <Box sx={{ marginLeft: 'auto' }}>
            <StatePreference
              sx={{marginLeft: '20px', paddingTop:'7px', paddingBottom:'7px', paddingLeft: '7px', minWidth:'380px'}}
              update={handleStateDDChange}
              label="Zoom To"
              prefStateKey={VE_PREF_STATE_KEY}
              hideWhenSingleOption={true}>
            </StatePreference>
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            {renderAnalyzeThisAreaButton()}
          </Box>
        </Stack>
      </>
    )
  }


  const rendered = () => {
    if (mapConfigLoading) {
      return (
        <ContainerWithCard>
          <LineLoader message="Loading..." />
        </ContainerWithCard>
      );
    }

    return (
      <>
        <Container
          className="full-width-container"
          sx={{ marginTop: '1px' }}
          maxWidth={false}
        >
          <Stack direction="row" spacing={0}>
            <SideBar
              ref={sideBarRef}
              drawerWidth={421}
              initiallyOpen={true}
              title={'Explorer Tools'}
              aboutThisSideBarLabel={'About This Tool'}
              onAbtThisClick={handleAboutThisToolClick}
              onSideBarToggle={handleSideBarToggled}
            >
              <ValuesExplorerTabPanel
                categories={categories.current}
                mapReady={mapReady}
                featData={featData}
                selectedTab={selectedTab}
                forceUpdate={forceUpdate}
                onClearButtonClick={handleClearButtonClick}
                onTabChanged={handleDrawerTabChange}
                onFiltersUpdated={handleFilterChange}
                onExcludeSwitchChange={handleExcludeSwitchChange}
              >
              </ValuesExplorerTabPanel>
            </SideBar>

            <Card className="full-page-map" sx={{ flexGrow: 3 }}>
              {renderMapActions()}

              <ThemeMap
                ref={themeMapInstance}
                defaultExtent={statePref?.extent}
                themes={mapConfig}
                activeLayers={activeLayers}
                featuresClicked={processFeatures}
                mapSet={setMap}
              />
            </Card>
          </Stack>
        </Container>

        <Modal
          okLabel="Close"
          cancelLabel="Cancel"
          title="About This Tool"
          onOk={handleAboutThisToolClose}
          onCancel={handleAboutThisToolClose}
          handleClose={handleAboutThisToolClose}
          showX={false}
          open={aboutOpen}
          hideActions={false}
          hideCancel={true}
        >
          <Typography gutterBottom>
            This Values Explorer tool is designed to show the distribution
            and density of highly valued resources and assets (HVRAs) in your
            areas of interest, in combination with the wildfire hazard potential (WHP).
          </Typography>
          <Typography gutterBottom>
            Each &apos;cell&apos; in the Hexagonal Composite map has a calculated score
            for the density of features for each HVRA, as well as a score of the median
            wildfire hazard potential in that cell. The color of each cell corresponds
            to a relative ranking of the combined scores: i.e., Lowest, Low, Medium, High,
            or Highest. The darker the color, the higher the relative density of HVRAs and
            wildfire hazard potential. <strong>NOTE:</strong> The relative rank is
            recalculated based on your map extent, so that you can see 'hot spots' relative
            to your field of view.
          </Typography>
          <br></br>
          <Typography gutterBottom>
            INSTRUCTIONS:
          </Typography>
          <ul>
            <li>If applicable, select a state from the drop-down list at the top of the map.</li>
            <li>Each HVRA/WHP is on (included) by default, but you can exclude an entire HVRA
              to see the relative importance of the remaining HVRAs, and/or exclude the WHP
              to explore only the Values. To do this, click the &apos;Include&apos; toggle
              to turn off and exclude its scores from the total rankings.</li>
            <li>You may also filter out &apos;cells&apos; based on their ranking, so you can
              focus in on areas that only have higher densities of HVRAs and WHP. To do this,
              click the sliders per HVRA/WHP to narrow the range of classes you want to show
              on the map. <strong>NOTE:</strong> Cells will be omitted from the map if their
              scores are not in your selected set.</li>
          </ul>

        </Modal>

        <Modal
          okLabel="OK"
          title="Welcome to Values Explorer!"
          onOk={handleWelcomeToVEClose}
          handleClose={handleWelcomeToVEClose}
          showX={false}
          open={welcomeToVEOpen}
          hideActions={false}
          hideCancel={true}
        >
          <Typography gutterBottom>
            This Values Explorer tool is designed to show the distribution and density of
            highly valued resources and assets (HVRAs) in your areas of interest, in
            combination with the wildfire hazard potential (WHP).
          </Typography>
          <Typography gutterBottom>
            To get started, choose a state from the drop-down list located at the top of the map.
            If your Values Explorer only has one state, your state will be loaded by default.
          </Typography>
        </Modal>
      </>
    );
  };

  return rendered();
};

export default Explorer;
